/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import https from 'https';
import getConfig from '../config';
import { getLogger } from '../utils/SearchLogger';
import WebStorage from '../storage/WebStorage';
import SearchUtils from '../utils/SearchUtils';

class FileTypeService {
	/*
	 **
	 **  P U B L I C   I N T E R F A C E
	 **
	 */

	/**
	 * Retrieves the localized strings of file types.
	 */
	async fetchFileTypeData(locale: string, forceFetchFromServer = false): Promise<FileTypeData> {
		let fileTypeData;

		if (this.isFileTypeDataEnabled) {
			try {
				if (WebStorage.localStorage.getItem('fileTypeData')) {
					WebStorage.localStorage.removeItem('fileTypeData');
				}
				fileTypeData = WebStorage.sessionStorage.getItem('fileTypeData');
				if (
					!forceFetchFromServer &&
					typeof fileTypeData === 'object' &&
					fileTypeData !== null &&
					fileTypeData.locale === this.searchHostContext.locale
				) {
					FileTypeService.fileTypeDataObject = fileTypeData;
					return fileTypeData;
				}

				fileTypeData = await this._performFileTypeRequest(locale);
				fileTypeData.timestamp = new Date().toISOString();

				if (fileTypeData && Object.keys(fileTypeData.translations).length > 0) {
					WebStorage.sessionStorage.setItem('fileTypeData', fileTypeData);
				}
			} catch (e) {
				this.logger.error('Search Server', `fetchFileTypeData() failed, ${e}`, undefined);
				fileTypeData = WebStorage.sessionStorage.getItem('fileTypeData');
			}
		} else {
			fileTypeData = this.fileTypeData;
		}

		FileTypeService.fileTypeDataObject = fileTypeData;
		return fileTypeData;
	}

	/*
	 **
	 **  P R I V A T E   M E T H O D S
	 **
	 */
	fileTypeData: FileTypeData;
	isFileTypeDataEnabled: boolean;
	env: string;
	searchHostContext: HostContext;
	logger: Logger;
	static fileTypeDataObject: FileTypeData = {
		translations: {},
		locale: '',
	};

	constructor(featureFlags: Array<string>, searchHostContext: HostContext) {
		this.isFileTypeDataEnabled = true;
		// give it a default value
		this.fileTypeData = {
			translations: {},
			locale: '',
		};
		this.logger = getLogger();
		this.searchHostContext = searchHostContext;
	}

	/**
	 * Retrieves fileTypeData from FileTypeService.
	 * @returns {FileTypeData}
	 */
	_performFileTypeRequest(locale: string): Promise<FileTypeData> {
		const isCDNEnabledFromFloodgate = SearchUtils.isFloodgateFeatureInTest(
			'ccs.cdn.translations',
			this.searchHostContext,
		);
		return new Promise((resolve, reject) => {
			const request = https.request(
				{
					hostname: isCDNEnabledFromFloodgate
						? getConfig(this.searchHostContext.environment, 'SEARCH_SERVER_CDN')
						: getConfig(this.searchHostContext.environment, 'SEARCH_SERVER'),
					method: 'GET',
					path: `/v1/translations?locale=${locale}`,
					headers: {
						Accept: 'application/json',
						'Content-Type': 'application/json',
						'x-api-key': this.searchHostContext.hostInfo.apiKey,
						authorization: `Bearer ${this.searchHostContext.imsInfo.accessToken}`,
					},
				},
				(res: any) => {
					if (res && res.statusCode && res.statusCode >= 300) {
						// On error, it should show the error status code
						const fileTypeServiceError = new Error(
							`_performFileTypeRequest failed, statusCode=${res.statusCode}`,
						);
						reject(fileTypeServiceError);
					} else {
						let data = '';
						res.setEncoding('utf8');
						res.on('data', (chunk) => (data += chunk));
						res.on('end', () => {
							// Success!
							try {
								this.fileTypeData.translations = JSON.parse(data).translations;
								this.fileTypeData.locale = locale;

								// if the response doesn't have the right data, we'll end up
								// returning the default data that the constructor provides
								resolve(this.fileTypeData);
							} catch (err) {
								reject(err);
							}
						});
					}
				},
			);
			request.on('error', (err) => {
				const fileTypeError = new Error(`FileTypeService connection error: ${err.toString()}`);
				reject(fileTypeError);
			});
			request.end();
		});
	}
}

export default FileTypeService;
